import {
  getPrice,
  deletePrice,
  updatePrice,
  addPrice,
} from '@/adapters/master';
import { IDataModal } from '@/interfaces/data-modal';
import { IPrice, IPriceRes } from '@/interfaces/master';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Flex,
  Form,
  Modal,
  notification,
  Radio,
  Select,
  Table,
  TableColumnsType,
} from 'antd';
import { useEffect, useMemo, useState, useCallback, Suspense } from 'react';
import { uniqueId } from 'lodash';
import dayjs from 'dayjs';

const _modal: IDataModal<IPrice> = {
  open: false,
};

const PricePage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<IPrice[]>([]);
  const [action, setAction] = useState<'create' | 'update' | 'match'>('create');
  const [selected, setSelected] = useState<IPrice[]>([]);
  const [modal, setModal] = useState<IDataModal<IPrice>>({ ..._modal });
  const [form] = Form.useForm();
  const [tab, setTab] = useState<'create' | 'update'>('create');

  // const { data, refetch } = useQuery<IBrandRes, Error>({
  //   queryKey: ['search-brand-list'],
  //   queryFn: searchBrands,
  //   retry: 0,
  // });
  const { data, refetch } = useQuery<IPriceRes, Error>({
    queryKey: ['get-price'],
    queryFn: getPrice,
    retry: 0,
  });

  const actionPriceMutation = useMutation({
    mutationFn: async (data: Partial<IPrice>) =>
      tab === 'create' ? await addPrice(data) : await updatePrice(data),
    mutationKey: tab === 'create' ? ['add-price'] : ['update-price'],
    onSuccess: data => {
      if (data.status === 'fulfilled' && tab === 'create') {
        notification.success({ message: 'Sync Price สำเร็จ' });
        refetch();
      } else if (data?.data[0].status === 'fulfilled' && tab === 'update') {
        notification.success({ message: 'Update Price สำเร็จ' });
        refetch();
      } else {
        notification.error({ message: data.reason?.errors?.errorMessage });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const handleSyncSelectedPrice = async (selectedRows: any) => {
    await actionPriceMutation.mutateAsync(selectedRows);
  };

  const actionDeletePrice = useMutation({
    mutationFn: async (data: Partial<IPrice>) => await deletePrice(data),
    mutationKey: ['delete-Price'],
    onSuccess: data => {
      if (data.status === 'OK' && tab === 'update') {
        notification.success({ message: 'Delete Price สำเร็จ' });
        refetch();
      } else {
        notification.error({ message: 'Delete Price ไม่สำเร็จ' });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  // const handleSyncTOERP = async () => {
  //   await actionTOERP.mutateAsync();
  // };

  const handleDeletePrice = async (selectedRows: any) => {
    await actionDeletePrice.mutateAsync(selectedRows);
  };

  const handleUpdatePriceCode = useCallback((item: IPrice) => {
    setModal({
      open: true,
      item: item,
    });
  }, []);

  const columns = useMemo(
    () =>
      [
        {
          title: 'ลำดับ',
          dataIndex: '',
          width: 40,
          align: 'center' as const,
          className: 'text-bold',
          render: (_: any, __: IPrice, i: number) => (
            <div className="font-weight-bold">{i + 1}</div>
          ),
        },
        {
          title: 'เอกสาร',
          dataIndex: 'PTB_NBR',
          defaultSortOrder: 'ascend',
          sorter: (a: { PTB_NBR: string }, b: { PTB_NBR: string }) =>
            Number(a.PTB_NBR.replace(/\D/g, '').replace('-', '')) -
            Number(b.PTB_NBR.replace(/\D/g, '').replace('-', '')),
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.PTB_NBR)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.PTB_NBR?.startsWith(value as string);
          },
        },
        {
          title: 'จังหวัด',
          dataIndex: 'PTB_PROVICE',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.PTB_PROVICE)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.PTB_PROVICE?.startsWith(value as string);
          },
        },
        {
          title: 'สินค้า',
          dataIndex: 'PTB_ITEM',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.PTB_ITEM)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.PTB_ITEM?.startsWith(value as string);
          },
        },
        {
          title: 'ชื่อสินค้า',
          dataIndex: 'PT_DESC1',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.PT_DESC1)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return value
              ? record?.PT_DESC1?.startsWith(value as string)
              : !record?.PT_DESC1;
          },
        },
        {
          title: 'วันที่',
          dataIndex: 'PTB_EFFDATE',
          render: PTB_EFFDATE => `${dayjs(PTB_EFFDATE).format('DD/MM/YYYY')}`,
          filterSearch: true,
          filters: Array.from(
            new Set(
              selected.map(item => {
                return dayjs(item.PTB_EFFDATE).format('DD/MM/YYYY');
              }),
            ),
          )
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return dayjs(record?.PTB_EFFDATE).format('DD/MM/YYYY') === value;
          },
        },
        {
          title: 'ราคา',
          dataIndex: 'PTB_PRICE',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.PTB_PRICE)))
            .sort((a, b) => Number(a) - Number(b))
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record.PTB_PRICE === value;
          },
        },
        {
          title: 'แหล่งที่มา',
          dataIndex: 'PTB_REF',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.PTB_REF))).map(
            code => {
              return { text: code, value: code };
            },
          ),
          onFilter: (value, record) => {
            return value
              ? record?.PTB_REF?.startsWith(value as string)
              : !record.PTB_REF;
          },
        },
      ] as TableColumnsType<IPrice>,
    [tab, handleUpdatePriceCode],
  );

  const insert = data?.insert;
  const update = data?.update;
  const match = data?.match;
  useEffect(() => {
    const listMap = { create: insert, update, match };
    setSelected(listMap[tab]?.map(c => ({ ...c, _key: uniqueId('b-') })) || []);
    setSelectedRowKeys([]);
  }, [tab, insert, update, match]);

  const rowSelection = {
    onChange: (_: React.Key[], selectedRows: IPrice[]) => {
      setSelectedRowKeys(selectedRows.map(row => row._key));
      setSelectedRows(selectedRows);
    },
    selectedRowKeys,
    getCheckboxProps: (record: IPrice) => ({
      disabled: action === 'match',
    }),
  };

  const [search, SetSearch] = useState('');
  const [searchText1, setSearchText1] = useState('');
  const [searchText2, setSearchText2] = useState('');

  const filteredData = useMemo(() => {
    return selected.filter(item => {
      const itemCode = (item.PTB_NBR || '').toLowerCase();
      const searchValue = search.trim().toLowerCase();
      return !searchValue || itemCode.includes(searchValue);
    });
  }, [selected, action, search, searchText1, searchText2]);

  // const onFinish: FormProps<IBrand>['onFinish'] = async values => {
  //   await matchBrandApi.mutateAsync({
  //     ...values,
  //     BRAND_SING_ID: modal.item?.BRAND_SING_ID,
  //   });
  // };

  return (
    <div>
      <Flex justify="space-between" gap={2} className="mb-3">
        <Flex gap={0}>
          <Radio.Group value={tab} onChange={e => setTab(e.target.value)}>
            <Radio.Button value="create">เพิ่ม</Radio.Button>
            <Radio.Button value="update">แก้ไข/ปรับปรุง</Radio.Button>
          </Radio.Group>
        </Flex>
        <Flex gap={12}>
          {tab === 'update' ? (
            <button
              type="button"
              className="btn btn-info"
              style={{ minWidth: 100 }}
              onClick={() => handleDeletePrice(selectedRows)}
            >
              {actionDeletePrice.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="sr-only">Loading...</span>
                </>
              ) : (
                'ลบข้อมูล Price'
              )}
            </button>
          ) : (
            <></>
          )}

          <button
            type="button"
            className="btn btn-success"
            style={{ minWidth: 100 }}
            disabled={!selectedRowKeys.length}
            onClick={() => handleSyncSelectedPrice(selectedRows)}
          >
            {actionPriceMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span className="sr-only">Loading...</span>
              </>
            ) : tab === 'create' ? (
              'ส่งข้อมูลไป OTOD >'
            ) : (
              'แก้ไขข้อมูลที่ OTOD >'
            )}
          </button>
        </Flex>
      </Flex>
      <Table<IPrice>
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        columns={columns}
        dataSource={filteredData}
        size="small"
        bordered
        rowKey={r => r._key}
        pagination={false}
        locale={{ emptyText: 'ไม่พบรายการ' }}
      />
      <Suspense>
        <Modal
          open={modal.open}
          width={350}
          onCancel={() => setModal({ ..._modal })}
          // loading={matchBrandApi.isPending}
          // onOk={() => {
          //   form
          //     .validateFields()
          //     .then(values => {
          //       onFinish(values);
          //     })
          // .catch(() => {
          //       // validation failed
          //     });
          // }}
        >
          <Form
            form={form}
            name="land"
            layout="vertical"
            // onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item<IPrice>
              label="เอกสาร"
              name="PTB_NBR"
              rules={[{ required: true }]}
            >
              <Select
                style={{ minWidth: 150 }}
                size="large"
                showSearch
                allowClear
                placeholder="ค้นหา"
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={[...(insert || [])].sort().map(item => ({
                  label: `${item.PTB_NBR} - ${item.PTB_NBR}`,
                  value: item.PTB_NBR,
                }))}
              />
            </Form.Item>
          </Form>
        </Modal>
      </Suspense>
    </div>
  );
};

export default PricePage;
